<template>
  <div
    v-if="hasGiftOptions"
    class="payment-gift-informer"
    :class="{
      'payment-gift-informer--cash': isCashPayment && isEligible,
      'payment-gift-informer--promo': !isCashPayment && isEligible,
      'payment-gift-informer--locked': !isEligible,
    }">
    <div class="payment-gift-informer__header flex q-gutter-sm no-wrap">
      <div class="payment-gift-informer__badge" >
        <q-icon :name="ICONS.gift" size="16px" />
      </div>

      <div class="payment-gift-informer__title s-font-2xs s-font-md-sm text-weight-bold" v-html="headerTitle"/>

      <div v-if="false" class="payment-gift-informer__subtitle s-font-3xs" v-html="headerSubtitle" />
    </div>

    <div class="q-mt-md">
      <div class="payment-gift-informer__options row q-col-gutter-sm">
        <div
            v-for="option in giftOptions"
            :key="option.code"
            class="col-24 col-md-12"
        >
          <button
              type="button"
              class="payment-gift-option"
              :class="{
          'payment-gift-option--selected': isGiftOptionSelected(option.code),
          'payment-gift-option--readonly': isGiftOptionReadonly,
        }"
              @click="onGiftClick(option.code)">
            <div class="payment-gift-option__image-wrap">
              <q-img
                  v-if="option.image"
                  :src="resolveGiftImage(option.image)"
                  class="payment-gift-option__image"
                  fit="contain">
                <template #error>
                  <div class="payment-gift-option__image-fallback">
                    <q-icon :name="ICONS.gift" size="20px" color="positive" />
                  </div>
                </template>
              </q-img>
              <div v-else class="payment-gift-option__image-fallback">
                <q-icon :name="ICONS.gift" size="20px" color="positive" />
              </div>
            </div>

            <div class="payment-gift-option__title s-font-xs s-font-md-sm">
              {{ option.title }}
            </div>

            <q-icon v-if="isGiftOptionSelected(option.code)" :name="ICONS.checkCircle" size="17px" color="positive"
                    class="payment-gift-option__check" />
          </button>
        </div>
      </div>
    </div>

    <div v-if="!isEligible" class="payment-gift-informer__progress">
      <div class="payment-gift-informer__progress-row">
        <span class="s-font-3xs text-grey-7">
          До подарка не хватает
          <span class="text-weight-bold text-primary">{{ formatPrice(amountRemaining) }}</span>
        </span>
        <span class="s-font-3xs text-grey-6">
          {{ formatPrice(pricePay) }} / {{ formatPrice(PAYMENT_GIFT_MIN_ORDER) }}
        </span>
      </div>
      <q-linear-progress :value="progressPercent / 100" color="positive" track-color="grey-3" rounded size="6px"
        class="payment-gift-informer__progress-bar" />
    </div>

    <div v-if="isEligible && !isCashPayment && cashButtonEnabled" class="payment-gift-informer__action">
      <q-btn unelevated no-caps dense color="positive" class="payment-gift-informer__btn" :icon="ICONS.payment"
        label="Выбрать оплату наличными" @click="switchToCash" />
    </div>

    <div v-else-if="isEligible && isCashPayment" class="payment-gift-informer__note s-font-3xs">
      <q-icon :name="ICONS.infoCircle" size="13px" class="q-mr-xs" />
      Подарок добавляется только при оплате наличными.
    </div>
  </div>
</template>

<script setup lang="ts">
import { ICONS } from "@/assets/icons";
import { useImager } from "@/core/store/imager";
import { useCurrency } from "@/modules/shop/store/currency";
import {
  PAYMENT_GIFT_MIN_ORDER,
  useVorderPaymentGift
} from "@/modules/shop/store/vorder-payment-gift";
import { storeToRefs } from "pinia";
import { computed } from "vue";

const props = withDefaults(defineProps<{
  /** Показывать кнопку переключения на оплату наличными */
  cashButtonEnabled?: boolean
}>(), {
  cashButtonEnabled: true,
})

const paymentGiftStore = useVorderPaymentGift()
const { formatPrice } = useCurrency()
const { imageResolve } = useImager()

const {
  giftOptions,
  hasGiftOptions,
  isSingleGift,
  singleGift,
  isEligible,
  isCashPayment,
  selectedGiftCode,
  amountRemaining,
  progressPercent,
  pricePay,
  giftAvailable
} = storeToRefs(paymentGiftStore)

const { selectGift, switchToCash } = paymentGiftStore

const minOrderFormatted = computed(() => formatPrice(PAYMENT_GIFT_MIN_ORDER))

const giftTitleQuoted = computed(() => {
  const title = singleGift.value?.title
  return title ? `«${title}»` : ''
})

const headerTitle = computed(() => {
  const minOrder = minOrderFormatted.value

  if (!isEligible.value) {
    return `Подарок при оплате наличными при заказе от ${minOrder}`
  }

  if (isCashPayment.value) {
    if (isSingleGift.value) {
      return 'Ваш подарок за оплату наличными'
    }
    return 'Выберите подарок за оплату наличными'
  }

  return `Подарок при <span class="text-primary">оплате наличными</span> от ${minOrder}`
})

const headerSubtitle = computed(() => {
  const minOrder = minOrderFormatted.value
  const cashBold = '<span class="text-weight-bold">наличными при получении</span>'

  if (!isEligible.value) {
    return 'Оплатите наличными при получении и получите блюдо в подарок.'
  }

  if (isCashPayment.value) {
    if (isSingleGift.value) {
      return `При заказе от ${minOrder} оплатите ${cashBold} — добавим ${giftTitleQuoted.value} в подарок.`
    }
    return `При заказе от ${minOrder} оплатите ${cashBold} — добавим выбранное блюдо в подарок.`
  }

  if (isSingleGift.value) {
    return `Переключитесь на оплату наличными при заказе от ${minOrder}, чтобы получить подарок.`
  }
  return `Переключитесь на оплату наличными при заказе от ${minOrder}, чтобы выбрать подарок.`
})

const canSelectGift = computed(() => isEligible.value && isCashPayment.value && !isSingleGift.value)

const isGiftOptionReadonly = computed(() => !canSelectGift.value)

const isGiftOptionSelected = (code: string) => {
  return isEligible.value && isCashPayment.value && selectedGiftCode.value === code
}

const resolveGiftImage = (src: string) => {
  if (src.startsWith('/static/')) {
    return src
  }
  return imageResolve(src, 's-1')
}

const onGiftClick = (code: string) => {
  if (!canSelectGift.value) return
  selectGift(code)
}
</script>

<style scoped lang="scss">
.payment-gift-informer {
  margin-top: 16px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(76, 175, 80, 0.2);
  background: #f8fbf8;
  box-shadow: 0 2px 10px rgba(76, 175, 80, 0.08);

  &--promo {
    border-color: rgba(255, 152, 0, 0.22);
    background: #fffaf5;
    box-shadow: 0 2px 10px rgba(255, 152, 0, 0.08);
  }

  &--cash {
    border-color: rgba(76, 175, 80, 0.32);
  }

  &--locked {
    border-color: rgba(0, 0, 0, 0.08);
    background: #fafafa;
    box-shadow: none;
  }
}

.payment-gift-informer__badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(76, 175, 80, 0.12);
  color: #2e7d32;
  font-size: 11px;
  font-weight: 600;
}

.payment-gift-informer--promo .payment-gift-informer__badge {
  background: rgba(255, 152, 0, 0.12);
  color: #ef6c00;
}

.payment-gift-informer--locked .payment-gift-informer__badge {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.55);
}

.payment-gift-informer__title {
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.88);
}

.payment-gift-informer__subtitle {
  margin-top: 4px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.55);
}

.payment-gift-informer__progress {
  margin-top: 10px;
}

.payment-gift-informer__progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.payment-gift-informer__progress-bar {
  border-radius: 999px;
}

.payment-gift-informer__options {
}

.payment-gift-option {
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background: #ffffff;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &--readonly {
    cursor: default;
    opacity: 0.72;
  }

  &:not(&--readonly):not(:disabled) {
    cursor: pointer;
  }

  &--selected {
    border-color: $primary;
  }
}

.payment-gift-option__image-wrap {
  flex-shrink: 0;
  overflow: hidden;
  width: 46px;
  height: 46px;
  border-radius: 10px;
  background: #f2f2f2;
}

.payment-gift-option__image,
.payment-gift-option__image-fallback {
  width: 100%;
  height: 100%;
}

.payment-gift-option__image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-gift-option__title {
  flex: 1;
  min-width: 0;
  font-weight: 500;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.84);
}

.payment-gift-option__check {
  flex-shrink: 0;
}

.payment-gift-informer__action {
  margin-top: 10px;
}

.payment-gift-informer__btn {
  width: 100%;
  border-radius: 10px;
  font-weight: 600;
}

.payment-gift-informer__note {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.5);
  line-height: 1.35;
}
</style>
